import useIdentity from "@/features/ui/hooks/use-identity";
import { PersistedLineSheetSetListQuery } from "@/features/order-sheet-sets/agency-line-sheet-set-list-page";
import { useMemo } from "react";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import { SheetTabFilterQuery } from "@/features/order-sheet-sets/sheet-tab-filter";
import { PersistedLineSheetSetDetailPageQuery } from "@/features/line-sheet-sets/detail/use-persisted-line-sheet-sheet-set-detail-page-query";
import { BestSellingSectionQuery } from "@/features/dashboard/features/best-selling-section/hooks/useBsQuery";

export const useChipList = (
  query: PersistedLineSheetSetListQuery &
    SheetTabFilterQuery &
    PersistedLineSheetSetDetailPageQuery &
    BestSellingSectionQuery
) => {
  const identity = useIdentity();
  const brandKey = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      let request: ListAllCompaniesRequest = {};
      request.type__in = ["BRAND"];
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity]);

  const { data: brandData } = useListAllCompanies(
    brandKey?.parameter,
    brandKey?.request
  );

  const boutiqueKey = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      let request: ListAllCompaniesRequest = {};
      request.type__in = ["BOUTIQUE"];
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity]);

  const { data: boutiqueData } = useListAllCompanies(
    boutiqueKey?.parameter,
    boutiqueKey?.request
  );

  const brandList = [
    ...(query["madeByCompany.id__in"]
      ?.map((id) => brandData?.find((item) => item.id === +id))
      ?.map((brand) => ({
        id: brand?.id,
        name: brand?.name,
        type: "BRAND",
      })) ?? []),
  ].filter((item) => item.id);

  const boutiqueList =
    query["issuedByCompany.id__in"]
      ?.map((id) => boutiqueData?.find((item) => item.id === +id))
      ?.map((boutique) => ({
        id: boutique?.id,
        name: boutique?.name,
        type: "BOUTIQUE",
      })) ?? [];

  const seasonList =
    query["season__in"]?.map((season) => ({
      id: season,
      name: season,
      type: "SEASON",
    })) ?? [];

  const orderSheetBrandList =
    query["brandName__eq"]?.map((brand) => ({
      id: brand,
      name: brand,
      type: "ORDER_BRAND",
    })) ?? [];

  // brandId__in (ID 기반 브랜드 필터) — brand 회사 id로 brandData에서 이름 찾아 칩 표시.
  const orderSheetBrandIdList =
    query["brandId__in"]
      ?.map((id) => brandData?.find((item) => item.id === +id))
      ?.map((brand) => ({
        id: brand?.id,
        name: brand?.name,
        type: "ORDER_BRAND_ID",
      })) ?? [];

  const orderSheetGenderList =
    query["gender__eq"]?.map((gender) => ({
      id: gender,
      name: gender,
      type: "ORDER_GENDER",
    })) ?? [];

  const orderSheetCategoryList =
    query["category__eq"]?.map((category) => ({
      id: category,
      name: category,
      type: "ORDER_CATEGORY",
    })) ?? [];

  const typeQuery =
    identity?.company?.type === "AGENCY" && query.type__eq
      ? [
          {
            id: query.type__eq,
            name: query.type__eq,
            type: "TYPE",
          },
        ]
      : [];

  const bestSellingBrandList =
    query.brandIdsBs
      ?.map((id) => brandData?.find((item) => item.id === +id))
      ?.map((brand) => ({
        id: brand?.id,
        name: brand?.name,
        type: "BEST_SELLING_BRAND",
      })) ?? [];

  const chipList = [
    ...brandList,
    ...orderSheetBrandList,
    ...orderSheetBrandIdList,
    ...boutiqueList,
    ...seasonList,
    ...orderSheetGenderList,
    ...orderSheetCategoryList,
    ...typeQuery,
    ...bestSellingBrandList,
  ].filter((item) => item && item.name);

  const refineQuery = (type?: string, id?: number | string) => {
    if (type === "BRAND") {
      return {
        "madeByCompany.id__in": (query["madeByCompany.id__in"] || []).filter(
          (brandId) => +brandId !== +id!
        ),
      };
    } else if (type === "BOUTIQUE") {
      return {
        "issuedByCompany.id__in": (
          query["issuedByCompany.id__in"] || []
        ).filter((boutiqueId) => +boutiqueId !== +id!),
      };
    } else if (type === "SEASON") {
      return {
        season__in: (query["season__in"] || []).filter(
          (season) => season !== id
        ),
      };
    } else if (type === "ORDER_BRAND") {
      return {
        brandName__eq: (query["brandName__eq"] || []).filter(
          (brand) => brand !== id
        ),
      };
    } else if (type === "ORDER_BRAND_ID") {
      return {
        brandId__in: (query["brandId__in"] || []).filter(
          (brandId) => +brandId !== +id!
        ),
      };
    } else if (type === "ORDER_GENDER") {
      return {
        gender__eq: (query["gender__eq"] || []).filter(
          (gender) => gender !== id
        ),
      };
    } else if (type === "ORDER_CATEGORY") {
      return {
        category__eq: (query["category__eq"] || []).filter(
          (category) => category !== id
        ),
      };
    } else if (type === "TYPE") {
      return {
        type__eq: undefined,
      };
    } else if (type === "BEST_SELLING_BRAND") {
      return {
        brandIdsBs: (query.brandIdsBs || []).filter(
          (brandId) => +brandId !== +id!
        ),
      };
    }
  };
  return {
    refineQuery,
    chipList,
  };
};
